import type { StorageConfig } from '../ImportFromFile';

/**
 * Uploads a file to S3 or LocalStorage using the storage configuration.
 * Returns the S3 key (path) where the file was uploaded.
 */
export const uploadFileToStorage = async (
  file: File,
  storageConfig: StorageConfig
): Promise<string> => {
  const formData = new FormData();

  // Calculate the S3 key by replacing filename placeholder
  const s3Key = storageConfig.formData.key
    .replace('${filename}', () => file.name) // S3Storage format
    .replace(':filename', () => file.name); // LocalStorage format

  // Add all the storage params (S3 signature, policy, etc.)
  Object.entries(storageConfig.formData).forEach(([key, value]) => {
    const stringValue = String(value);
    if (key === 'key') {
      // Use the calculated key with filename replaced
      formData.append(key, s3Key);
    } else if (key === 'Filename') {
      // Replace ${filename} placeholder in Filename field
      formData.append(
        key,
        stringValue.replace('${filename}', () => file.name)
      );
    } else {
      formData.append(key, stringValue);
    }
  });

  // Add Content-Type
  formData.append('Content-Type', file.type || 'application/octet-stream');

  // Add the file itself
  formData.append('file', file);

  const response = await fetch(storageConfig.url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Error uploading file');
  }

  // Parse the storage response to get the actual key that was used
  // Returns: <PostResponse><Key>actual_key_here</Key></PostResponse>
  // Use DOMParser (not a regex) so XML entities (e.g. "&amp;" for "&") are decoded correctly.
  const responseText = await response.text();
  const keyElement = new DOMParser()
    .parseFromString(responseText, 'application/xml')
    .querySelector('Key');

  if (keyElement?.textContent) {
    return keyElement.textContent;
  }

  // Fallback: Use calculated key if XML parsing fails
  return s3Key;
};
